import {useEffect, useRef} from 'react'

type PollingRowsManagerOptions<TRow> = {
    rows: TRow[]
    pollingCondition: (row: TRow) => boolean
    getId: (row: TRow) => number | string
    pollFunction: (id: number | string) => Promise<TRow | null>
    onUpdate: (updatedRow: TRow) => void
    intervalMs?: number
}

export function usePollingRowsManager<TRow>({
                                                rows,
                                                pollingCondition,
                                                getId,
                                                pollFunction,
                                                onUpdate,
                                                intervalMs = 10000,
                                            }: PollingRowsManagerOptions<TRow>) {
    const pollingIdsRef = useRef(new Set<number | string>())
    const intervalRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        const startPolling = () => {
            if (intervalRef.current) return

            intervalRef.current = setInterval(() => {
                const pendingRows = rows.filter(pollingCondition)

                if (pendingRows.length === 0) {
                    stopPolling()
                    return
                }

                for (const row of pendingRows) {
                    const id = getId(row)

                    if (pollingIdsRef.current.has(id)) continue

                    pollingIdsRef.current.add(id)

                    pollFunction(id)
                        .then((updatedRow) => {
                            if (updatedRow) {
                                onUpdate(updatedRow)

                                const stillPending = pollingCondition(updatedRow)

                                if (stillPending) {
                                    pollingIdsRef.current.delete(id)
                                }
                            }
                        })
                        .catch(() => {
                            pollingIdsRef.current.delete(id)
                        })
                }
            }, intervalMs)
        }

        const stopPolling = () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current)
                intervalRef.current = null
            }
        }

        const hasPendingRows = rows.some(pollingCondition)

        if (hasPendingRows) {
            startPolling()
        } else {
            stopPolling()
        }

        return () => {
            stopPolling()
        }
    }, [rows, pollingCondition, getId, pollFunction, onUpdate, intervalMs])
}
